import { useDispatch, useSelector } from 'react-redux';
import AppRoutes from './routes/AppRoutes';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppDispatch, RootState } from './redux-stores/store';
import { useEffect, useRef, useState } from 'react';
import useCheckVersion from './hooks/useCheckVersion';
import { APP_ENV, BRANCH_APP_LINK_HOSTS, WEB_APP_URL } from './constants/constants';
import { initAuth } from './redux-stores/slices/authSlice';
import Loader from './components/loaders/Loader';
import { StatusBar, Style } from "@capacitor/status-bar";
import { useMenuScrollLock } from './hooks/useMenuScrollLock';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { initPushListeners } from './lib/pushNotifications';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

import { initDeviceDetection } from './utils/deviceDetection';
import withSlideUpModal from './utils/hoc/withSlideUpModalComponent';
import UpdateRequired from './components/UpdateRequired';
import { checkLatestAppVersion } from './services/api/device';
import { App as CapacitorApp } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { analyticsLog, initAnalytics } from "./lib/analytics";
import { isAndroid, isNativePlatform } from './utils/platformUtil';
import { initAdsTracking, initBranchAttribution, setParamsFromBranchInitEvent } from './utils/adsTracking';
import { BranchDeepLinks } from 'capacitor-branch-deep-links';

const UpdateRequiredPopup = withSlideUpModal(UpdateRequired);

function PushNotificationListener() {
    const navigate = useNavigate();

    useEffect(() => {
        initPushListeners(
            (data) => {
                if (data?.route) {
                    console.log('Push notification redirecting to:' + data.route);
                    let path = data.route;
                    try {
                        const url = new URL(data.route);
                        path = url.pathname + url.search + url.hash;
                    } catch (e) {

                    }
                    setTimeout(() => { navigate(path); }, 0);
                }
            },
            (notification) => {
                console.log('Foreground push received:', notification);
            }
        );
    }, [navigate]);

    return null;
}
const RouteTracker = () => {
    const location = useLocation();
    useEffect(() => {
        if (APP_ENV !== 'production') return;

        analyticsLog('page_view', {
            page_path: location.pathname
        })
    }, [location]);
    return null;
};

function App() {
    const mode = useSelector((state: RootState) => state.theme.mode);
    const authInitialized = useSelector((state: RootState) => state.auth.authInitialized);
    const dispatch = useDispatch<AppDispatch>();
    const [updateRequiredPopup, showUpdateRequiredPopup] = useState<any>(null);
    const navigate = useNavigate();
    const isBranchHandledRef = useRef(false);

    useCheckVersion();
    useMenuScrollLock();
    usePullToRefresh();

    useEffect(() => {
        document.body.classList.toggle('theme-dark', mode === 'dark');
        document.body.classList.toggle('theme-light', mode === 'light');
    }, [mode]);

    useEffect(() => {
        dispatch(initAuth());
    }, [dispatch]);

    useEffect(() => {
        const initStatusBar = async () => {
            try {
                await StatusBar.setStyle({ style: Style.Light });
                await StatusBar.setBackgroundColor({ color: '#ffffff' });
                await StatusBar.setOverlaysWebView({ overlay: false });
            } catch (err) {
                console.warn('StatusBar not available:', err);
            }
        };
        if (isNativePlatform()) {
            initStatusBar();
        }
    }, []);

    useEffect(() => {
        if (isNativePlatform()) {
            Keyboard.setResizeMode({ mode: isAndroid() ? KeyboardResize.Body : KeyboardResize.Native });
        }
    }, []);

    // useEffect(() => {
    //     if(!isNativePlatform()) return;
    //     const showSub = Keyboard.addListener('keyboardWillShow', (info) => {
    //         console.log('keyboardWillShow', info);
    //       document.body.style.paddingBottom = `${info.keyboardHeight}px`;
    //     });
      
    //     const hideSub = Keyboard.addListener('keyboardWillHide', () => {
    //         console.log('keyboardWillHide');
    //       document.body.style.paddingBottom = '0px';
      
    //     });
      
    //     return () => {
      
    //       showSub.then(sub => sub.remove());
      
    //       hideSub.then(sub => sub.remove());
      
    //     };
      
    //   }, []);

    useEffect(() => {
        initAdsTracking();
        initDeviceDetection();
        initAnalytics();
    }, []);

    useEffect(() => {
        const checkDeviceAppVersion = async () => {
            if (!isNativePlatform()) return;
            try {
                const info = await Device.getInfo();
                const appInfo = await CapacitorApp.getInfo();
                const res = await checkLatestAppVersion(info.platform, appInfo.version);
                if (res.hard_update) {
                    showUpdateRequiredPopup({
                        latest_version: res.latest_version!,
                        store_url: res.store_url ?? WEB_APP_URL
                    });
                }
            } catch (error) {
                console.error('App version check failed', error);
            }
        };

        checkDeviceAppVersion();
    }, []);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;

        navigator.serviceWorker.getRegistrations().then(registrations => {
            registrations.forEach(reg => reg.unregister());
        });

        // Important: clear PWA caches too
        if ("caches" in window) {
            caches.keys().then(keys => {
                keys.forEach(key => caches.delete(key));
            });
        }
    }, []);

    useEffect(() => {
        // This listener fires every time the app opens
        if (isNativePlatform()) {
            BranchDeepLinks.addListener('init', async (event) => {
                if (event.referringParams && event.referringParams['+clicked_branch_link']) {
                    isBranchHandledRef.current = true;
                    const params = event.referringParams;
                    await setParamsFromBranchInitEvent(event);
                    if (params['$deeplink_path']) {
                        navigate(params['$deeplink_path'], { replace: true });
                    }
                }
            });
        }
    }, []);


    // handle deep links
    useEffect(() => {
        const sub = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
            try {
                const parsedUrl = new URL(url);
                console.log('------------------------------- app url open event -------------------------------');
                console.log(`------------------------------- url: ${url} -------------------------------`);
                console.log(`------------------------------- parsedUrl: ${JSON.stringify(parsedUrl)} -------------------------------`);
                const path = url.startsWith("influns://") ? url.replace('influns:/', '') : parsedUrl.pathname + parsedUrl.search;

                if (BRANCH_APP_LINK_HOSTS.includes(parsedUrl.hostname)) {
                    return navigate('/', { replace: true });
                }

                setTimeout(() => {
                    if (isBranchHandledRef.current) {
                        isBranchHandledRef.current = false; // reset
                        return;
                    }
                    if (path) {
                        navigate(path, { replace: true });
                    }
                }, 1000);
            } catch (err) {
                console.error('Deep link handling failed:', err);
            }
        });

        return () => {
            sub.then(listener => listener.remove());
        };
    }, [navigate]);

    if (!authInitialized) {
        return <Loader isLoading={true} />;
    }


    return (
        <div id="page">
            <RouteTracker />
            <PushNotificationListener />

            {authInitialized ? (
                <AppRoutes />
            ) : (
                <Loader isLoading={true} />
            )}

            <UpdateRequiredPopup
                isActive={!!updateRequiredPopup}
                onClose={() => { }}
                {...updateRequiredPopup}
            />
        </div>
    );
}

export default App;
